import { createContext, RefObject, useContext } from 'react'

import { useTheme } from '@chakra-ui/react'

import { useShallowMemo } from 'src/hooks/useShallowMemo'

type ResponsiveContainerState = {
    isSmallContainer: boolean
    isMediumContainer: boolean
    isLargeContainer: boolean
}

type ResponsiveContainerContextProps = {
    containerWidth: number
    containerRef: RefObject<HTMLElement | null>
} & ResponsiveContainerState

const ResponsiveContainerContext = createContext<ResponsiveContainerContextProps | null>(null)

type ResponsiveContainerProviderProps = {
    children: React.ReactNode
    containerWidth: number
    containerRef: RefObject<HTMLElement | null>
}

export const ResponsiveContainerProvider = (props: ResponsiveContainerProviderProps) => {
    const { children, containerWidth, containerRef } = props

    const theme = useTheme()

    const isSmallContainer = containerWidth <= theme.breakPoints.mobilePhoneMaxWidth
    const isMediumContainer = containerWidth <= theme.breakPoints.tabletMaxWidth
    const isLargeContainer = !isSmallContainer && !isMediumContainer

    const contextValue = useShallowMemo<ResponsiveContainerContextProps>({
        containerWidth,
        isSmallContainer,
        isMediumContainer,
        isLargeContainer,
        containerRef,
    })

    return <ResponsiveContainerContext.Provider value={contextValue}>{children}</ResponsiveContainerContext.Provider>
}

export const useResponsiveContainer = (): ResponsiveContainerContextProps => {
    const context = useContext(ResponsiveContainerContext)

    if (!context) {
        throw new Error('useResponsiveContainer must be used within a ResponsiveContainerProvider')
    }

    return context
}
